import type { InfiniteData, QueryClient } from '@tanstack/react-query';

import type {
  ChatConversationLastMessage,
  ChatConversationStatus,
  ChatMessage,
  Unsubscribe,
} from '@/services/chat/chatTransport';
import {
  getClosedInquiriesV2QueryOptions,
  getOpenInquiriesV2QueryOptions,
  getAllInquiriesV2QueryOptions,
  getAllClosedInquiriesV2QueryOptions,
  type GetInquiriesV2Response,
} from '@/services/getInquiriesV2';
import { getInquiryCancerTypesQueryOptions } from '@/services/getInquiryCancerTypes';
import { getInquiryStatsQueryOptions } from '@/services/getInquiryStats';
import { getInquiryV2QueryOptions, type GetInquiryV2Response } from '@/services/getInquiryV2';
import {
  drivesInboxPreview,
  isMessageForViewer,
  readIsDraft,
  readMessageType,
} from '@/types/messages/attributes';
import { shouldFetchUserScoped } from '@/util/authTokens';
import {
  cancelUnreadInquiriesInvalidation,
  invalidateUnreadInquiriesDebounced,
} from '@/util/inboxInvalidation';

type InfiniteInquiriesData = InfiniteData<GetInquiriesV2Response>;

/**
 * Predicate to identify filtered inquiry list queries.
 * Filtered queries have shape ['inquiriesV2', state, userId, cancerTypeId]
 * with cancerTypeId present.
 */
const isFilteredInquiriesQuery = (query: { queryKey: unknown }) => {
  const queryKey = query.queryKey;

  return (
    Array.isArray(queryKey) &&
    queryKey[0] === 'inquiriesV2' &&
    queryKey.length >= 4 &&
    queryKey[3] !== undefined &&
    queryKey[3] !== null
  );
};

/** What the inbox reads of a conversation when one of its events arrives. */
export interface InboxConversation {
  readonly id: string;
  readonly lastMessage?: ChatConversationLastMessage;
  readonly status?: ChatConversationStatus;
}

export interface InboxMessageEvent {
  conversation: InboxConversation;
  message: ChatMessage;
}

/**
 * Conversation-level events across every conversation the viewer is in, as
 * the inbox needs them. These are what the inbox list, unread counts, stats
 * and cancer-type filters react to; a single open chat listens to its own
 * conversation through the transport instead.
 */
export type InboxEvent =
  | { type: 'conversationUpdated'; conversation: InboxConversation }
  | { type: 'conversationRemoved'; conversation: InboxConversation }
  | { type: 'conversationJoined'; conversation: InboxConversation }
  | ({ type: 'messageAdded' } & InboxMessageEvent)
  | ({ type: 'messageUpdated' } & InboxMessageEvent);

/**
 * A transport's stream of inbox events.
 *
 * Only the Twilio session feeds one. On the Primum transport the inbox is kept
 * current by the inbox socket instead (`useInboxLiveUpdates`), whose hints
 * carry no content and are applied by refetching rather than patching.
 */
export interface ChatEventSource {
  /**
   * The viewer as this source writes them on a message's author, when that is
   * not simply the user id. Read at each event, since a source may learn it
   * only once connected.
   */
  readonly viewerIdentity?: string;
  subscribe(listener: (event: InboxEvent) => void): Unsubscribe;
}

export interface InboxSyncOptions {
  source: ChatEventSource;
  queryClient: QueryClient;
  userId: string | undefined;
}

/**
 * Keeps the inbox caches in step with a transport's events: list rows,
 * previews and unread flags are patched in place, and the counts, stats and
 * cancer types are refetched, debounced so an initial sync's burst of events
 * becomes one request each.
 *
 * Nothing here knows which transport is behind the source. The returned stop
 * unsubscribes and drops any pending invalidation, so a session that is torn
 * down cannot refetch on behalf of the next one.
 */
export function startInboxSync({ source, queryClient, userId }: InboxSyncOptions): () => void {
  let statsInvalidateTimeout: ReturnType<typeof setTimeout> | undefined;

  let cancerTypesInvalidateTimeout: ReturnType<typeof setTimeout> | undefined;

  let inquiriesInvalidateTimeout: ReturnType<typeof setTimeout> | undefined;

  const invalidateUnreadDebounced = () => {
    invalidateUnreadInquiriesDebounced(queryClient, 750);
  };

  const invalidateStatsDebounced = () => {
    if (statsInvalidateTimeout) {
      clearTimeout(statsInvalidateTimeout);
    }

    statsInvalidateTimeout = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: getInquiryStatsQueryOptions(userId).queryKey });
    }, 750);
  };

  const invalidateCancerTypesDebounced = () => {
    if (cancerTypesInvalidateTimeout) {
      clearTimeout(cancerTypesInvalidateTimeout);
    }

    cancerTypesInvalidateTimeout = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: getInquiryCancerTypesQueryOptions(userId).queryKey,
      });
    }, 750);
  };

  const invalidateInquiriesDebounced = () => {
    if (inquiriesInvalidateTimeout) {
      clearTimeout(inquiriesInvalidateTimeout);
    }

    inquiriesInvalidateTimeout = setTimeout(() => {
      if (!shouldFetchUserScoped(userId)) {
        return;
      }

      queryClient.invalidateQueries({
        queryKey: getOpenInquiriesV2QueryOptions(userId).queryKey,
      });

      queryClient.invalidateQueries({
        queryKey: getClosedInquiriesV2QueryOptions(userId).queryKey,
      });

      queryClient.invalidateQueries({ queryKey: getAllInquiriesV2QueryOptions(userId).queryKey });

      queryClient.invalidateQueries({ predicate: isFilteredInquiriesQuery });
    }, 1000);
  };

  const statusByConversation = new Map<string, ChatConversationStatus | undefined>();

  /**
   * Returns true if the message author matches the current user.
   * Checks both the source's own identity for the viewer and userId, since
   * either may be what the source writes as the author.
   */
  const isFromCurrentUser = (author: string | null) => {
    if (!author) {
      return false;
    }

    const viewerIdentity = source.viewerIdentity;

    const userIdStr = userId ? String(userId) : undefined;

    return author === viewerIdentity || author === userIdStr;
  };

  /**
   * Handles a conversation's read state, status or last message changing.
   *
   * New messages set hasUnread optimistically in the inquiries cache.
   * Clearing unread is driven by explicit mark-as-read flows, not Twilio
   * lastRead changes, because Twilio can advance the read horizon passively
   * on web for conversations the user did not explicitly open.
   *
   * When conversation status changes we update the single-inquiry cache and
   * invalidate stats. We also write last message date and status into the
   * inquiry list cache so the list view stays in sync.
   */
  const onConversationUpdated = ({ conversation }: { conversation: InboxConversation }) => {
    const sid = conversation.id;

    const nextStatus = conversation.status;

    const updateInquiryStatus = () => {
      if (nextStatus) {
        queryClient.setQueryData<GetInquiryV2Response>(
          getInquiryV2QueryOptions({ conversationId: sid }).queryKey,
          (prev) => (prev ? { ...prev, inquiryStatus: nextStatus } : prev)
        );
      }
    };

    const hasPrevStatus = statusByConversation.has(sid);

    const prevStatus = statusByConversation.get(sid);

    if (!hasPrevStatus) {
      statusByConversation.set(sid, nextStatus);

      updateInquiryStatus();
    } else if (prevStatus !== nextStatus) {
      statusByConversation.set(sid, nextStatus);

      invalidateStatsDebounced();

      updateInquiryStatus();
    }

    const lastMessageDate = conversation.lastMessage?.createdAt;

    const updateInquiryList = (prev: InfiniteInquiriesData | undefined) => {
      if (!prev?.pages) {
        return prev;
      }

      return {
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          inquiries: page.inquiries?.map((inquiry) => {
            if (inquiry.conversationId === sid) {
              const hasNewMessage = !!lastMessageDate;

              const statusChanged = nextStatus && nextStatus !== inquiry.inquiryStatus;

              if (!hasNewMessage && !statusChanged) {
                return inquiry;
              }

              return {
                ...inquiry,
                ...(hasNewMessage && { lastResponseDate: lastMessageDate.toISOString() }),
                ...(statusChanged && { inquiryStatus: nextStatus }),
              };
            }

            return inquiry;
          }),
        })),
      };
    };

    queryClient.setQueryData<InfiniteInquiriesData>(
      getOpenInquiriesV2QueryOptions(userId).queryKey,
      updateInquiryList
    );

    queryClient.setQueryData<InfiniteInquiriesData>(
      getClosedInquiriesV2QueryOptions(userId).queryKey,
      updateInquiryList
    );

    queryClient.setQueryData<InfiniteInquiriesData>(
      getAllInquiriesV2QueryOptions(userId).queryKey,
      updateInquiryList
    );

    queryClient.setQueryData<InfiniteInquiriesData>(
      getAllClosedInquiriesV2QueryOptions(userId).queryKey,
      (prev) => {
        if (!prev?.pages) {
          return prev;
        }

        return {
          ...prev,
          pages: prev.pages.map((page) => ({
            ...page,
            inquiries: page.inquiries?.map((inquiry) => {
              if (inquiry.conversationId === conversation.id) {
                return {
                  ...inquiry,
                  inquiryStatus: conversation.status || inquiry.inquiryStatus,
                };
              }

              return inquiry;
            }),
          })),
        };
      }
    );
  };

  const onConversationRemoved = ({ conversation }: { conversation: InboxConversation }) => {
    const openInquiriesData = queryClient.getQueryData<InfiniteInquiriesData>(
      getOpenInquiriesV2QueryOptions(userId).queryKey
    );

    const allInquiriesData = queryClient.getQueryData<InfiniteInquiriesData>(
      getAllInquiriesV2QueryOptions(userId).queryKey
    );

    const removedInquiry =
      openInquiriesData?.pages
        ?.flatMap((page) => page.inquiries ?? [])
        .find((inquiry) => inquiry.conversationId === conversation.id) ||
      allInquiriesData?.pages
        ?.flatMap((page) => page.inquiries ?? [])
        .find((inquiry) => inquiry.conversationId === conversation.id);

    if (!removedInquiry) {
      return;
    }

    if (openInquiriesData?.pages) {
      queryClient.setQueryData<InfiniteInquiriesData>(
        getOpenInquiriesV2QueryOptions(userId).queryKey,
        (prev) => {
          if (!prev?.pages) {
            return prev;
          }

          return {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              inquiries: page.inquiries?.filter(
                (inquiry) => inquiry.conversationId !== conversation.id
              ),
            })),
          };
        }
      );
    }

    if (allInquiriesData?.pages) {
      queryClient.setQueryData<InfiniteInquiriesData>(
        getAllInquiriesV2QueryOptions(userId).queryKey,
        (prev) => {
          if (!prev?.pages) {
            return prev;
          }

          return {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              inquiries: page.inquiries?.map((inquiry) => {
                if (inquiry.conversationId === conversation.id) {
                  return {
                    ...inquiry,
                    inquiryStatus: 'closed' as const,
                  };
                }

                return inquiry;
              }),
            })),
          };
        }
      );
    }

    queryClient.setQueryData<InfiniteInquiriesData>(
      getClosedInquiriesV2QueryOptions(userId).queryKey,
      (prev) => {
        if (!prev?.pages) {
          return prev;
        }

        const firstPage = prev.pages[0];

        return {
          ...prev,
          pages: [
            {
              ...firstPage,
              inquiries: [
                {
                  ...removedInquiry,
                  inquiryStatus: 'closed' as const,
                },
                ...(firstPage.inquiries ?? []),
              ],
            },
            ...prev.pages.slice(1),
          ],
        };
      }
    );

    invalidateInquiriesDebounced();

    invalidateUnreadDebounced();

    invalidateStatsDebounced();
  };

  /**
   * When this client joins a new conversation we invalidate so the inbox and
   * tab badge reflect the new conversation and unread state without a full reload.
   */
  const onConversationJoined = () => {
    invalidateUnreadDebounced();

    invalidateInquiriesDebounced();

    invalidateStatsDebounced();

    invalidateCancerTypesDebounced();
  };

  /**
   * Writes a message into its row in each loaded inquiry list: its date as
   * the row's last response, its body as the preview, the conversation's
   * status, and unread when someone else wrote it. One routine for a new
   * message and for an update that changes the last one, so the two cannot
   * render a row differently.
   */
  const applyMessageToInboxLists = (
    conversation: InboxConversation,
    message: ChatMessage,
    fromCurrentUser: boolean
  ) => {
    const lastMessageDate = conversation.lastMessage?.createdAt;

    const newStatus = conversation.status;

    const newPreview = message.body || '';

    const shouldMarkUnread = !fromCurrentUser;

    const updateInquiryList = (prev: InfiniteInquiriesData | undefined) => {
      if (!prev?.pages) {
        return prev;
      }

      return {
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          inquiries: page.inquiries?.map((inquiry) => {
            if (inquiry.conversationId === conversation.id) {
              return {
                ...inquiry,
                lastResponseDate: lastMessageDate?.toISOString() || inquiry.lastResponseDate,
                lastMessagePreview: newPreview || inquiry.lastMessagePreview,
                ...(newStatus && { inquiryStatus: newStatus }),
                ...(shouldMarkUnread && { hasUnread: true }),
              };
            }

            return inquiry;
          }),
        })),
      };
    };

    queryClient.setQueryData<InfiniteInquiriesData>(
      getOpenInquiriesV2QueryOptions(userId).queryKey,
      updateInquiryList
    );

    queryClient.setQueryData<InfiniteInquiriesData>(
      getClosedInquiriesV2QueryOptions(userId).queryKey,
      updateInquiryList
    );

    queryClient.setQueryData<InfiniteInquiriesData>(
      getAllInquiriesV2QueryOptions(userId).queryKey,
      updateInquiryList
    );

    invalidateUnreadDebounced();
  };

  /**
   * On new message: optimistically update inquiry list cache with message preview
   * and hasUnread. Unread counts are invalidated separately via debounced handler.
   *
   * Draft messages from the other party are skipped entirely — the recipient
   * shouldn't see them as a preview or get an unread badge until the draft
   * flag is cleared. The author still sees their own drafts reflected.
   *
   * A support message addressed to the other participant is skipped for the
   * same reason: it never appears in this viewer's thread, so a preview or
   * unread badge for it would point at nothing. The backend already scopes
   * the authoritative inbox row to the audience, so nothing here has to be
   * invalidated either.
   */
  const onMessageAdded = ({ conversation, message }: InboxMessageEvent) => {
    const fromCurrentUser = isFromCurrentUser(message.author);

    if (readIsDraft(message.attributes) && !fromCurrentUser) {
      return;
    }

    if (!isMessageForViewer(message.attributes, userId)) {
      return;
    }

    // Still invalidate, so the backend's authoritative count loads.
    if (!drivesInboxPreview(readMessageType(message.attributes))) {
      invalidateUnreadDebounced();

      return;
    }

    applyMessageToInboxLists(conversation, message, fromCurrentUser);
  };

  /**
   * On message update: if the draft flag was just cleared, surface the
   * message in the inbox the same way `messageAdded` would. We only act
   * when the updated message is the conversation's last message — edits
   * to historical messages don't change the row's preview.
   */
  const onMessageUpdated = ({ conversation, message }: InboxMessageEvent) => {
    if (readIsDraft(message.attributes)) {
      return;
    }

    if (!isMessageForViewer(message.attributes, userId)) {
      return;
    }

    // Unlike `messageAdded`, an update to a suppressed type invalidates
    // nothing: the message was never counted, so no count can be stale.
    if (!drivesInboxPreview(readMessageType(message.attributes))) {
      return;
    }

    if (conversation.lastMessage?.index !== message.index) {
      return;
    }

    const fromCurrentUser = isFromCurrentUser(message.author);

    applyMessageToInboxLists(conversation, message, fromCurrentUser);
  };

  const unsubscribe = source.subscribe((event) => {
    switch (event.type) {
      case 'conversationUpdated':
        onConversationUpdated(event);

        return;
      case 'conversationRemoved':
        onConversationRemoved(event);

        return;
      case 'conversationJoined':
        onConversationJoined();

        return;
      case 'messageAdded':
        onMessageAdded(event);

        return;
      case 'messageUpdated':
        onMessageUpdated(event);
    }
  });

  return () => {
    unsubscribe();

    cancelUnreadInquiriesInvalidation(queryClient);

    if (statsInvalidateTimeout) {
      clearTimeout(statsInvalidateTimeout);
    }

    if (cancerTypesInvalidateTimeout) {
      clearTimeout(cancerTypesInvalidateTimeout);
    }

    if (inquiriesInvalidateTimeout) {
      clearTimeout(inquiriesInvalidateTimeout);
    }
  };
}
